import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import {
  Search, Calendar, Filter, X, Plus, Edit3, Trash2, Loader2,
  Hospital, AlertTriangle, Stethoscope, Activity, ArrowRight, RefreshCw, ChevronDown, Zap,
  Sparkles, ClipboardCheck, CheckCircle2, XCircle, ShieldCheck,
} from 'lucide-react';
import PendingCheckinsCard from '@/components/dashboard/PendingCheckinsCard';
import HealthFileSection from '@/components/dashboard/HealthFileSection';
import ThreadTimeline from '@/components/dashboard/ThreadTimeline';
import ErrorBoundary from '@/components/ErrorBoundary';

/**
 * PatientLogTab — unified daily-log for every patient interaction.
 *
 * Default view: last 3 days of activity merged from manual visits + Connect
 * submissions + bookings + voice-calls. Filter bar widens the window, narrows
 * by visit_type / channel, and free-text search across name / phone-last4 /
 * symptom / notes.
 *
 * Phase 1: read + manual visit CRUD. Phase 2 (later): WhatsApp share of
 * admission instructions + per-row right drawer with full clinical detail.
 */

const VISIT_TYPE_LABEL = {
  consult: 'Consult',
  follow_up: 'Follow-up',
  minor_procedure: 'Minor procedure',
  major_procedure: 'Major procedure',
  supra_major: 'Supra-major',
  emergency: 'Emergency',
  admission: 'Admission',
};
const CHANNEL_LABEL = {
  myOnline: 'Online / WhatsApp',
  myClinicPage: 'My Website',
  PolyClinic: 'Poly / Clinic walk-in',
  Hospital: 'Hospital walk-in',
  myRefNetwork: 'My referral network',
  ReferredByDr: 'Ref by Doctor',
  ReferredByHospital: 'Ref by Hospital',
  ReferredByPolyClinic: 'Ref by Poly / Clinic',
  ReferredByPatient: 'Ref by Patient',
};
const TYPE_ICON = {
  consult: Stethoscope,
  follow_up: RefreshCw,
  minor_procedure: Activity,
  major_procedure: Activity,
  supra_major: Zap,
  emergency: AlertTriangle,
  admission: Hospital,
};
const TYPE_COLOUR = {
  consult: 'bg-sky-50 text-sky-700 border-sky-200',
  follow_up: 'bg-violet-50 text-violet-700 border-violet-200',
  minor_procedure: 'bg-amber-50 text-amber-700 border-amber-200',
  major_procedure: 'bg-orange-50 text-orange-700 border-orange-200',
  supra_major: 'bg-rose-50 text-rose-700 border-rose-200',
  emergency: 'bg-red-50 text-red-700 border-red-200',
  admission: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// Location.type → Came-via channel (mirrors backend LOCATION_TYPE_TO_CHANNEL).
// Kept in sync so the UI can preview the auto-derived channel before Save;
// the backend is still the single source of truth and re-derives on POST.
const LOCATION_TYPE_TO_CHANNEL = {
  'Hospital':       'Hospital',
  'Polyclinic':     'PolyClinic',
  'Clinic':         'PolyClinic',
  'Surgery Center': 'Hospital',
  'Institution':    'Hospital',
};

export default function PatientLogTab() {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [enums, setEnums] = useState({ visit_types: [], channels: [], emergency_subtypes: [] });
  const [windowLabel, setWindowLabel] = useState('');
  // Doctor's payment-privacy preference. When false, hide the ₹ column on
  // the table — but every row still surfaces a `.Paid` chip if it has a
  // recorded amount. The Amounts tab remains visible regardless.
  const [showAmountColumn, setShowAmountColumn] = useState(true);
  // Filters
  const [dateFrom, setDateFrom] = useState(daysAgo(3));
  const [dateTo, setDateTo] = useState(today());
  const [search, setSearch] = useState('');
  // visit_type filters intentionally not exposed in DocSite — they activate
  // when the doctor subscribes to HealthifyX (see /app/memory/healthifyx_patient_module_seed.md).
  const [channelFilters, setChannelFilters] = useState([]);
  // Show D-flagged (Dummy) rows in the table — off by default so the
  // canonical patient cohort stays clean. When on, D rows surface with a
  // subtle amber background so the doctor can audit them.
  const [showDummy, setShowDummy] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Composer
  const [composerOpen, setComposerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (search) params.set('search', search);
      if (channelFilters.length) params.set('channels', channelFilters.join(','));
      if (showDummy) params.set('include_potentials', 'true');
      const { data } = await api.get(`/dashboard/patient-log?${params.toString()}`);
      setEntries(data.entries || []);
      setEnums(data.enums || {});
      setWindowLabel(`${data.window_from} → ${data.window_to}`);
    } catch (e) {
      console.warn('patient-log fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, search, channelFilters, showDummy]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  // One-shot fetch of the doctor's payment-privacy preference. Defaults to
  // showing the column. Fails silent on error — never block the table.
  useEffect(() => {
    api.get('/dashboard/profile')
      .then(r => setShowAmountColumn(r.data?.show_amount_in_patient_log !== false))
      .catch(() => setShowAmountColumn(true));
  }, []);

  const resetFilters = () => {
    setDateFrom(daysAgo(3)); setDateTo(today()); setSearch('');
    setChannelFilters([]);
  };

  const toggleArr = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  // Compact "4 Jun" label for the mobile date pills (en-GB → day + short month).
  const fmtShort = (v) => {
    if (!v) return '';
    try {
      const d = new Date(`${v}T00:00:00`);
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  return (
    <div className="space-y-3" data-testid="patient-log-tab">
      {/* Header — title left, compact filters in the middle open space, CTA right */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {/* Title + window subtitle — hidden on mobile to save vertical space */}
        <div className="shrink-0 hidden lg:block">
          <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Patient Log
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-50 to-sky-50 text-violet-700 border border-violet-200" title="Subscribe to HealthifyX for auto-attached clinical depth — ICD-11, ICHI, SNOMED, FHIR, AI coding suggester, admission instructions">
              <Zap size={10} /> + HealthifyX for clinical depth
            </span>
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Showing <b className="text-slate-700">{windowLabel || 'last 3 days'}</b> — use filters to pull older entries.
          </p>
        </div>

        {/* Filters — one row on mobile (search 40% / dates 60%); fills the open middle space on desktop */}
        <div className="w-full lg:flex-1 lg:min-w-[240px] flex items-center lg:justify-center gap-2" data-testid="patient-log-filters">
          <div className="relative basis-[40%] shrink-0 lg:flex-1 lg:basis-auto lg:min-w-[170px] lg:max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, last-4 of phone, symptom, notes…"
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
              data-testid="patient-log-search" />
          </div>
          <div className="basis-[60%] lg:basis-auto flex items-center gap-1.5 lg:gap-2">
            <div className="relative flex-1 lg:flex-none">
              {/* Mobile: compact "4 Jun" pill with an invisible native picker on top — never overflows */}
              <div className="lg:hidden relative h-[38px]">
                <div className="flex items-center justify-center gap-1 h-full px-2 rounded-xl border border-slate-200 bg-white">
                  <Calendar size={13} className="text-slate-400 shrink-0" />
                  <span className="text-[12px] font-semibold text-slate-700 whitespace-nowrap">{fmtShort(dateFrom) || 'Start'}</span>
                </div>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="From date"
                  data-testid="patient-log-date-from" />
              </div>
              {/* Desktop: full native date field */}
              <div className="hidden lg:block relative">
                <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[150px] pl-8 pr-2 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                  data-testid="patient-log-date-from-desktop" />
              </div>
            </div>
            <div className="relative flex-1 lg:flex-none">
              {/* Mobile: compact "7 Jun" pill with an invisible native picker on top — never overflows */}
              <div className="lg:hidden relative h-[38px]">
                <div className="flex items-center justify-center gap-1 h-full px-2 rounded-xl border border-slate-200 bg-white">
                  <Calendar size={13} className="text-slate-400 shrink-0" />
                  <span className="text-[12px] font-semibold text-slate-700 whitespace-nowrap">{fmtShort(dateTo) || 'End'}</span>
                </div>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="To date"
                  data-testid="patient-log-date-to" />
              </div>
              {/* Desktop: full native date field */}
              <div className="hidden lg:block relative">
                <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                  className="w-[150px] pl-8 pr-2 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-sky-500/20"
                  data-testid="patient-log-date-to-desktop" />
              </div>
            </div>
          </div>
        </div>

        <button onClick={() => { setEditTarget(null); setComposerOpen(true); }}
          className="w-full lg:w-auto shrink-0 flex items-center justify-center gap-1.5 rounded-full px-4 py-2 bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
          data-testid="patient-log-add-btn">
          <Plus size={14} /> Log a visit
        </button>
      </div>

      {/* Live pending check-ins from the Clinic Check-In QR — auto-polls
          every 12 s so a patient scanning at the consult desk appears
          inside ~10 s on the doctor's screen. */}
      <PendingCheckinsCard onFinalized={() => fetchLog()} />

      {/* Thin filter toggles row (no big band) */}
      <div className="flex flex-wrap items-center gap-2" data-testid="patient-log-filter-toggles">
        <button onClick={() => setShowAdvanced(v => !v)}
          className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 px-2 py-1 rounded-lg hover:bg-slate-100"
          data-testid="patient-log-toggle-advanced">
          <Filter size={12} /> {channelFilters.length > 0 ? `${channelFilters.length} channel filter${channelFilters.length === 1 ? '' : 's'} active` : 'Channel filters'} <ChevronDown size={12} className={showAdvanced ? 'rotate-180 transition' : 'transition'} />
        </button>
        {/* D-row visibility toggle — off by default (Patient Log stays
            clean); ON renders dummy rows with a subtle amber tint. */}
        <button onClick={() => setShowDummy(v => !v)}
          className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg transition-colors ${showDummy ? 'text-amber-800 bg-amber-50 hover:bg-amber-100' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          data-testid="patient-log-toggle-dummy"
          aria-pressed={showDummy}
          title="Surface dummy/test (D) entries with an amber tint">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${showDummy ? 'bg-amber-400' : 'border border-slate-300'}`}></span>
          {showDummy ? 'Showing D · Dummy' : 'Show D · Dummy'}
        </button>
        {(channelFilters.length > 0 || search) && (
          <button onClick={resetFilters}
            className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 px-2 py-1 rounded-lg hover:bg-rose-50"
            data-testid="patient-log-reset-filters">
            <X size={12} /> Reset
          </button>
        )}
      </div>

      {showAdvanced && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2">
          <div>
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Channel</p>
            <div className="flex flex-wrap gap-1.5">
              {(enums.channels || []).map((c) => (
                <button key={c} onClick={() => toggleArr(channelFilters, setChannelFilters, c)}
                  className={`text-xs px-2.5 py-1 rounded-full border ${channelFilters.includes(c) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
                  data-testid={`patient-log-filter-channel-${c}`}>
                  {CHANNEL_LABEL[c] || c}
                </button>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-slate-400 italic">
            Tip: visit-type filters (consult / minor / major / supra-major / emergency / admission) activate when you subscribe to HealthifyX.
          </p>
        </div>
      )}

      {/* Recent visits — the main patient data log */}
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mt-1 mb-1 ml-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent visits</p>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            <p className="font-medium">No patient activity in this window.</p>
            <p className="text-xs text-slate-400 mt-1">Widen the date range or tap <b>Log a visit</b> to add one manually.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="patient-log-table">
              <thead className="bg-slate-50 text-[11px] sm:text-xs font-bold uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-3.5">Date</th>
                  <th className="text-left px-3 py-3.5">Patient</th>
                  <th className="text-left px-3 py-3.5">Phone</th>
                  <th className="text-left px-3 py-3.5">Clinic</th>
                  <th className="text-center px-3 py-3.5 whitespace-nowrap" title="How many times this patient has met you so far — across ALL your clinics.">Visit #</th>
                  <th className="text-left px-3 py-3.5">Condition/Symptom Reported</th>
                  <th className="text-left px-3 py-3.5">Came via</th>
                  <th className="text-center px-3 py-3.5 whitespace-nowrap" title="Digital Health File generated & sent to the patient. Generate it via Edit → Generate Digital Health File.">DHF Sent</th>
                  {showAmountColumn && (
                    <th className="text-right px-3 py-3.5 whitespace-nowrap" title="Amount received for this visit. Toggle visibility in Profile.">Amount</th>
                  )}
                  <th className="text-right px-3 py-3.5"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((e) => {
                  const TypeIcon = e.visit_type ? (TYPE_ICON[e.visit_type] || Stethoscope) : null;
                  const typeClr = TYPE_COLOUR[e.visit_type] || 'bg-slate-50 text-slate-700 border-slate-200';
                  // Render date + time (HH:MM) when the visit carries a
                  // server-stamped `when_at` (clinic check-ins always do;
                  // manual visits get it from created_at). Fall back to
                  // the date-only string for older rows.
                  let dateLabel = e.when || '—';
                  let timeLabel = '';
                  if (e.when_at) {
                    try {
                      const d = new Date(e.when_at);
                      if (!isNaN(d.getTime())) {
                        timeLabel = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
                      }
                    } catch { /* leave timeLabel empty */ }
                  }
                  return (
                    <tr key={e.id}
                        className={`${e.entry_status === 'D' ? 'bg-amber-50/60 hover:bg-amber-100/70' : 'hover:bg-slate-50'}`}
                        data-testid={`patient-log-row-${e.id}`}
                        title={e.entry_status === 'D' ? 'Dummy entry — excluded from totals, Amounts, and billing.' : undefined}>
                      <td className="px-3 py-3.5 text-sm sm:text-base text-slate-600 whitespace-nowrap">
                        <div>{dateLabel}</div>
                        {timeLabel && <div className="text-xs text-slate-400 font-mono mt-0.5">{timeLabel}</div>}
                      </td>
                      <td className="px-3 py-3.5 font-medium text-slate-900 text-base sm:text-lg max-w-[180px]">
                        <div className="truncate" title={e.patient_name || ''}>{e.patient_name || '—'}</div>
                        {(e.patient_age !== null && e.patient_age !== undefined && e.patient_age !== '') && (
                          <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-slate-500" data-testid={`patient-log-age-${e.id}`}>
                            {e.patient_age} yrs
                            {e.age_source === 'doctor' && (
                              <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wider" title="Age entered by the doctor (DpD) — not yet patient-verified.">DpD</span>
                            )}
                          </span>
                        )}
                        {e.is_dpd && (
                          <span
                            className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wider"
                            title="DpD = Doctor-Provided Data. The patient has not yet self-verified this entry via QR check-in. Their next QR scan will create a separate patient-verified row alongside this one."
                            data-testid={`patient-log-dpd-${e.id}`}
                          >
                            DpD · awaiting verify
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-sm sm:text-base text-slate-500 whitespace-nowrap font-mono">{e.patient_phone_masked || '—'}</td>
                      <td className="px-3 py-3.5 text-sm sm:text-base text-slate-600 max-w-[160px] truncate" title={e.location_name || ''}>
                        {e.location_name || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-3.5 text-center whitespace-nowrap">
                        {e.visit_count ? (
                          <span
                            className="inline-flex items-center justify-center min-w-[1.9rem] h-7 px-2 rounded-full text-sm font-bold bg-sky-50 text-sky-700 border border-sky-200"
                            title={`Visit ${e.visit_count} of ${e.visit_count_total || e.visit_count} so far with you (across all your clinics)`}
                            data-testid={`patient-log-visit-count-${e.id}`}
                          >
                            #{e.visit_count}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-sm sm:text-base text-slate-700 max-w-[260px]">
                        <div className="flex items-center gap-1.5">
                          <div className="truncate" title={e.primary_symptom}>{e.primary_symptom || '—'}</div>
                          {e.primary_symptom && e.symptom_source === 'patient' && (
                            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wider"
                              title="Patient-entered on check-in. Locked — cannot be edited."
                              data-testid={`patient-log-sympsrc-${e.id}`}>
                              Patient-Data
                            </span>
                          )}
                          {e.primary_symptom && e.symptom_source === 'doctor' && (
                            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wider"
                              title="DpD = Doctor-Provided Data. Sealed medical record — cannot be edited."
                              data-testid={`patient-log-sympsrc-${e.id}`}>
                              DpD
                            </span>
                          )}
                        </div>
                        {/* Clinical chips only render when HealthifyX has attached. */}
                        {(e.visit_type || e.grade_numeric || e.admission_instructions) && (
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            {e.visit_type && TypeIcon && (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${typeClr}`}>
                                <TypeIcon size={10} /> {VISIT_TYPE_LABEL[e.visit_type] || e.visit_type}
                                {e.emergency_subtype && <span className="ml-0.5 opacity-70">· {e.emergency_subtype}</span>}
                              </span>
                            )}
                            {e.grade_numeric && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200" title="HealthifyX grade · drives staffing + billing">
                                G·{e.grade_numeric}
                              </span>
                            )}
                            {e.admission_instructions && (
                              <span className="inline-flex items-center text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded" title="HealthifyX admission instructions attached">
                                <Hospital size={10} className="mr-0.5" /> ADMIT
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-sm sm:text-base text-slate-600 max-w-[180px] truncate" title={e.channel}>
                        {CHANNEL_LABEL[e.channel] || e.channel}
                        {e.referrer_name ? <span className="text-slate-400"> · {e.referrer_name}</span> : null}
                      </td>
                      <td className="px-3 py-3.5 text-center whitespace-nowrap" data-testid={`patient-log-dhf-${e.id}`}>
                        {e.editable ? (
                          e.health_file_id ? (
                            <span className="inline-flex items-center justify-center gap-1 text-emerald-600" title="Digital Health File generated & sent to patient">
                              <CheckCircle2 size={16} />
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center text-slate-300" title="Not generated yet — open Edit to generate & send">
                              <XCircle size={16} />
                            </span>
                          )
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      {showAmountColumn && (
                        <td className="px-3 py-3.5 text-right whitespace-nowrap" data-testid={`patient-log-amount-${e.id}`}>
                          {e.amount_paid > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-bold" title={`${e.payment_mode === 'online' ? 'Online' : 'Cash'} payment`}>
                              .Paid · ₹{(e.amount_paid).toLocaleString('en-IN')}
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-300">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-3.5 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={async () => {
                              const next = e.entry_status === 'D' ? 'R' : 'D';
                              try {
                                await api.patch(`/dashboard/patient-log/classify/${encodeURIComponent(e.id)}`, { entry_status: next });
                                fetchLog();
                              } catch (err) {
                                console.warn('classify failed', err);
                              }
                            }}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-colors ${
                              e.entry_status === 'D'
                                ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                                : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                            }`}
                            title={e.entry_status === 'D'
                              ? 'Dummy — excluded from totals. Click to mark Real.'
                              : 'Real patient. Click to mark Dummy / test entry.'}
                            data-testid={`patient-log-rd-${e.id}`}>
                            {e.entry_status === 'D' ? 'D' : 'R'}
                          </button>
                          {e.editable && (
                            <button onClick={async () => {
                              // Booking rows are PDP-sealed but live in
                              // db.bookings. Promote to a `visits` row on
                              // first Edit so DHF / clinical depth work
                              // natively. Idempotent server-side.
                              if (typeof e.id === 'string' && e.id.startsWith('booking:')) {
                                try {
                                  const bid = e.id.slice('booking:'.length);
                                  const { data } = await api.post(`/dashboard/patient-log/promote-booking/${bid}`);
                                  await fetchLog();
                                  // Open the freshly-promoted visit row.
                                  setEditTarget({ ...e, id: data.visit_id, source_kind: 'visit', editable: true });
                                  setComposerOpen(true);
                                  return;
                                } catch (err) {
                                  console.warn('promote-booking failed', err);
                                  return;
                                }
                              }
                              setEditTarget(e);
                              setComposerOpen(true);
                            }}
                              className="text-slate-400 hover:text-sky-600 p-1" title="Edit"
                              data-testid={`patient-log-edit-${e.id}`}>
                              <Edit3 size={14} />
                            </button>
                          )}
                          {e.editable && e.health_file_id && (
                            <button onClick={async (btn) => {
                              const b = btn.currentTarget;
                              b.setAttribute('data-busy', '1');
                              b.disabled = true;
                              b.textContent = '…';
                              // Full-screen overlay so the doctor knows we're
                              // working even if the tiny inline "…" is missed.
                              let ov = document.createElement('div');
                              ov.setAttribute('data-testid', 'rx-generating-overlay');
                              ov.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:9999;display:flex;align-items:center;justify-content:center;font-family:Manrope,system-ui,sans-serif';
                              ov.innerHTML = '<div style="background:#fff;padding:26px 32px;border-radius:16px;box-shadow:0 20px 60px rgba(0,0,0,0.25);text-align:center;min-width:280px"><div style="width:44px;height:44px;border:4px solid #e2e8f0;border-top-color:#059669;border-radius:50%;margin:0 auto 14px;animation:sp 0.9s linear infinite"></div><div style="font-weight:700;color:#0f172a;font-size:15px;margin-bottom:4px">Generating prescription…</div><div style="font-size:12px;color:#64748b">Building from the sealed DHF</div></div><style>@keyframes sp{to{transform:rotate(360deg)}}</style>';
                              document.body.appendChild(ov);
                              try {
                                const { data } = await api.post(`/dashboard/checkins/${e.id}/issue-rx`);
                                const url = `${process.env.REACT_APP_BACKEND_URL}${data.downloadUrl}`;
                                window.open(url, '_blank', 'noopener');
                              } catch (err) {
                                console.warn('issue-rx failed', err);
                                window.alert('Could not issue prescription: ' + (err?.response?.data?.detail || err.message));
                              } finally {
                                ov.remove();
                                b.removeAttribute('data-busy');
                                b.disabled = false;
                                b.textContent = 'Rx';
                              }
                            }}
                              className="ml-1 text-slate-400 hover:text-emerald-600 p-1 text-xs font-bold border border-slate-200 rounded disabled:opacity-50"
                              title="Issue Prescription (auto-attaches to DHF)"
                              data-testid={`patient-log-issue-rx-${e.id}`}>
                              Rx
                            </button>
                          )}
                          {e.health_file_id && e.rx_first_generated_at && (
                            <span className="ml-1 text-[10px] font-semibold whitespace-nowrap"
                              title={e.rx_patient_opened_at ? 'Patient first opened the prescription' : 'Patient has not opened the prescription yet'}
                              style={{ color: e.rx_patient_opened_at ? '#047857' : '#94a3b8' }}
                              data-testid={`patient-log-rx-po-${e.id}`}>
                              Po: {e.rx_patient_opened_at
                                ? new Date(e.rx_patient_opened_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
                                : 'not yet'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {composerOpen && (
        <VisitComposer
          target={editTarget}
          enums={enums}
          onClose={() => { setComposerOpen(false); setEditTarget(null); }}
          onSaved={() => { setComposerOpen(false); setEditTarget(null); fetchLog(); }}
        />
      )}
    </div>
  );
}

function VisitComposer({ target, enums, onClose, onSaved }) {
  const isEdit = !!target;
  const [form, setForm] = useState({
    patient_name: target?.patient_name || '',
    patient_phone: '',  // We never pre-fill encrypted phone — doctor re-enters to update.
    age: (target?.patient_age ?? '') === null ? '' : (target?.patient_age ?? ''),
    visit_date: target?.when || today(),
    // visit_type / emergency_subtype / admission_instructions are intentionally NOT
    // collected here. They belong to the HealthifyX clinical overlay. DocSite-only
    // doctors log channel + free-text "what happened". See
    // /app/memory/healthifyx_patient_module_seed.md for the HealthifyX composer.
    primary_symptom: target?.primary_symptom || '',
    channel: target?.channel || 'myClinicPage',
    referrer_name: target?.referrer_name || '',
    notes: target?.notes && target.editable ? target.notes : '',
    // Payment-of-record (Feb 2026). Doctor sets the integer amount + the
    // R/D flag any time — Save persists both. D-flagged rows are excluded
    // from Patient Log + Amounts + future billing receipts so dummy data
    // never contaminates the patient cohort.
    amount_paid: target?.amount_paid || 0,
    entry_status: target?.entry_status || 'R',
    // Facility (registered clinic/hospital/polyclinic). When set, the
    // "Came via" channel is auto-derived from location.type and cannot
    // be overridden — the doctor picks a facility, the channel follows.
    location_id: target?.location_id || '',
  });

  // Locations list — used to populate the Location dropdown and to derive
  // the locked "Came via" channel from the picked facility's type. Fetched
  // once on mount; falls back silently if the endpoint is unavailable.
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    let live = true;
    api.get('/dashboard/locations')
      .then(r => { if (live) setLocations(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setLocations([]); });
    return () => { live = false; };
  }, []);

  // Derived facility + auto-locked channel. When a location is picked, the
  // Came-via dropdown shows that channel greyed out (locked).
  const selectedLocation = locations.find(l => l.id === form.location_id);
  const derivedChannel = selectedLocation ? (LOCATION_TYPE_TO_CHANNEL[selectedLocation.type] || 'PolyClinic') : '';
  // Keep form.channel in sync with the locked channel so Save always
  // submits the correct value.
  useEffect(() => {
    if (derivedChannel && derivedChannel !== form.channel) {
      setForm(f => ({ ...f, channel: derivedChannel }));
    }
  }, [derivedChannel]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Read-only audit trail (consent + access fingerprint) ──────────────
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  useEffect(() => {
    if (!isEdit || !target?.patient_phone_hash) return undefined;
    let live = true;
    const run = async () => {
      setAuditLoading(true);
      try {
        const { data } = await api.get('/dashboard/patient-log/audit', { params: { phone_hash: target.patient_phone_hash } });
        if (live) setAudit(data.entries || []);
      } catch {
        if (live) setAudit([]);
      } finally {
        if (live) setAuditLoading(false);
      }
    };
    run();
    return () => { live = false; };
  }, [isEdit, target?.patient_phone_hash]);

  // ── Phone capture (revised, June 2026) ─────────────────────────────────
  // The OTP step is removed from the doctor's "Log a visit" composer. The
  // phone the doctor types is stamped as DpD (Doctor-Provided Data —
  // unverified by the patient). The patient self-verifies on their next
  // QR check-in; only that future row gets phone_source='patient' (sealed).
  // No SMS is sent from this composer — saves MSG91 cost and avoids
  // patients receiving an OTP for a visit they haven't done yet.
  const phoneDigits = (form.patient_phone || '').replace(/\D/g, '');
  const phoneValid = phoneDigits.length === 10;

  const showReferrer = (form.channel || '').startsWith('Ref') || form.channel === 'myRefNetwork';

  // Identity locked when this visit originated from a patient-side surface
  // (clinic check-in QR, connect page, chat) — the name/phone were entered
  // by the patient on their own device, so the doctor's dashboard isn't
  // authoritative for them. The "+ Log a visit" composer (manual entry)
  // keeps them editable because the doctor is the source of truth there.
  const PATIENT_SOURCE_CHANNELS = new Set([
    'clinic_checkin', 'clinic_checkin_rejected',
    'myClinicPage', 'connect', 'connect_otp',
    'chat', 'patient_chat', 'booking', 'voice_call',
  ]);
  const identityLocked = isEdit && PATIENT_SOURCE_CHANNELS.has(target?.channel);

  // Holds the latest Digital Health File clinical draft from HealthFileSection
  // so "Draft Save · Clinical Data" persists it on the visit (editable later).
  const dhfDraftRef = useRef(target?.dhf_draft || null);
  const handleDhfDraftChange = useCallback((draft) => { dhfDraftRef.current = draft; }, []);

  // ── Health Threads panel (Feb 2026) ────────────────────────────────────
  // Doctor-only surface. Lists this patient's longitudinal threads and
  // opens ThreadTimeline (straight-line visits + med changes + tests +
  // milestones). No patient consent required — all data is DpD authored
  // by this doctor from prior sealed DHFs.
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState('');
  const loadThreads = useCallback(async () => {
    if (!target?.patient_phone_hash) return;
    setThreadsLoading(true);
    try {
      const { data } = await api.get(`/dashboard/health-threads/patient/${target.patient_phone_hash}`);
      setThreads(data.threads || []);
    } catch { setThreads([]); }
    finally { setThreadsLoading(false); }
  }, [target?.patient_phone_hash]);
  useEffect(() => {
    if (!isEdit || !target?.patient_phone_hash) return;
    loadThreads();
  }, [isEdit, target?.patient_phone_hash, loadThreads]);

  const runBackfill = async () => {
    if (!target?.patient_phone_hash) return;
    setBackfilling(true); setBackfillResult('');
    try {
      const { data } = await api.post(`/dashboard/health-threads/backfill?patient_phone_hash=${encodeURIComponent(target.patient_phone_hash)}`);
      setBackfillResult(`Scanned ${data.scanned} DHF(s) · created ${data.created} · already-present ${data.skipped}`);
      await loadThreads();
    } catch (e) {
      setBackfillResult('Backfill failed');
    } finally { setBackfilling(false); }
  };


  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Rapid Save readiness — the 4 must-fill fields for a one-tap stub row.
  // Doctor can log a walk-in in seconds after the consultation without
  // opening the full clinical composer; the patient later self-verifies
  // (and unlocks DHF/Rx on the SAME calendar day only).
  const rapidReady = !isEdit
    && !!(form.patient_name || '').trim()
    && phoneValid
    && !!form.location_id
    && !!form.visit_date;

  const rapidSave = async () => {
    setError(''); setSaving(true);
    try {
      const payload = {
        rapid_save: true,
        patient_name: form.patient_name,
        patient_phone: phoneDigits,
        visit_date: form.visit_date,
        location_id: form.location_id,
        // Channel is auto-derived server-side from location.type. We omit
        // it to make the intent explicit; backend will stamp it.
        entry_status: form.entry_status || 'R',
      };
      await api.post('/dashboard/patient-log', payload);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Rapid Save failed');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setError(''); setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.emergency_subtype) payload.emergency_subtype = null;
      // Ensure location_id is passed through — channel is auto-derived
      // server-side from location.type when supplied.
      if (form.location_id) payload.location_id = form.location_id;
      // Normalize phone to 10 digits. NO OTP token any more — composer
      // entries are DpD by definition. Backend marks them
      // `phone_source='doctor_dpd'` (editable; not sealed) so the patient
      // can self-verify the same number on their next QR check-in.
      if (!isEdit) {
        payload.patient_phone = phoneDigits;
      } else {
        // PHONE LOCK (founder rule, May 2026): the patient phone captured
        // at first source of truth is immutable. The edit modal renders
        // the field read-only, but be doubly safe — never include it in
        // the PATCH payload so an accidental empty string can never reach
        // the backend.
        delete payload.patient_phone;
      }
      if (isEdit) {
        // Persist the editable clinical draft alongside the visit so typed
        // medicines/tests are never lost before the file is generated.
        if (dhfDraftRef.current) payload.dhf_draft = dhfDraftRef.current;
        const id = target.id.replace(/^visit:/, '');
        await api.patch(`/dashboard/patient-log/${id}`, payload);
      } else {
        await api.post('/dashboard/patient-log', payload);
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // onDelete intentionally removed — see no-delete policy above. The "soft
  // reject" path on the pending-checkin banner is the only way to mark a
  // stray scan, and even that keeps the row visible in the patient log.

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="patient-log-composer">
      <div className="w-full sm:w-[96vw] sm:max-w-7xl bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[94vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-xl sm:text-3xl font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isEdit ? 'Edit visit' : 'Log a visit'}
            </h3>
            {!isEdit && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200 text-[10px] sm:text-sm font-bold uppercase tracking-wider shrink-0"
                title="DpD = Doctor-Provided Data. Everything you type here is unverified by the patient until they self-confirm on their next QR check-in."
                data-testid="composer-dpd-badge"
              >
                DpD · Doctor-Provided Data
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="patient-log-composer-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 sm:p-8 space-y-4">
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs sm:text-base px-3 py-2">{error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
            {/* ── Rapid Save zone (rows 1 & 2) ─────────────────────────────
                Doctor-side one-tap workflow for walk-ins the patient
                didn't self-check-in for. Fill: Name + Mobile + Came Via
                (auto-derived from Location) + Location → tap Rapid Save.
                Everything else (symptom, notes, age, amount) can be
                added later — or by the patient on their next same-day
                QR check-in, which unlocks DHF & Digital Pharmacy. */}
            {!isEdit && (
              <div className="sm:col-span-2 rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50/40 p-4 sm:p-5 space-y-3" data-testid="composer-rapid-zone">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-sky-600 text-white">
                      <Zap size={12} /> Rapid Save zone
                    </span>
                    <span className="text-[11px] sm:text-sm text-slate-600">4 fields · patient self-verifies on next QR check-in</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
                  <Field label={`Patient name${identityLocked ? ' (entered by patient — locked)' : ''}`} required dpd={!isEdit}>
                    <input type="text" value={form.patient_name}
                      onChange={(e) => update('patient_name', e.target.value)}
                      readOnly={identityLocked}
                      className={`${fieldCls} ${identityLocked ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`}
                      data-testid="composer-patient-name" />
                  </Field>
                  <Field label="Patient mobile" required dpd={!isEdit}>
                    <div className="space-y-2">
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={form.patient_phone}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                          update('patient_phone', v);
                        }}
                        placeholder="10-digit mobile"
                        className={fieldCls}
                        data-testid="composer-phone" />
                      {!phoneValid && form.patient_phone && (
                        <p className="text-[10px] text-rose-600">Phone must be exactly 10 digits.</p>
                      )}
                      <p className="text-[10px] text-slate-500 leading-snug">
                        No SMS will be sent. This number is stamped as <b>DpD · unverified</b>; the patient self-verifies on their next QR check-in.
                      </p>
                    </div>
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 sm:gap-6 sm:items-end">
                  <Field label={`Came via${selectedLocation ? ' · locked from facility' : ''}`} required dpd={!isEdit}>
                    <select
                      value={form.channel}
                      onChange={(e) => update('channel', e.target.value)}
                      disabled={!!selectedLocation}
                      className={`${fieldCls} ${selectedLocation ? 'bg-slate-100 text-slate-600 cursor-not-allowed' : ''}`}
                      data-testid="composer-channel">
                      {(enums.channels || []).map(c => (
                        <option key={c} value={c}>{CHANNEL_LABEL[c] || c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Location" required dpd={!isEdit}>
                    <select
                      value={form.location_id}
                      onChange={(e) => update('location_id', e.target.value)}
                      className={fieldCls}
                      data-testid="composer-location">
                      <option value="">— pick facility —</option>
                      {locations.map(l => (
                        <option key={l.id} value={l.id}>{l.name}{l.type ? ` · ${l.type}` : ''}</option>
                      ))}
                    </select>
                    {selectedLocation && (
                      <p className="text-[10px] text-slate-500 mt-1" data-testid="composer-location-type">
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {selectedLocation.type || 'Facility'}
                        </span>
                        <span className="ml-1">Facility type is fixed — cannot be re-classified.</span>
                      </p>
                    )}
                  </Field>
                  <div className="flex sm:justify-end">
                    <button
                      type="button"
                      onClick={rapidSave}
                      disabled={!rapidReady || saving}
                      className="w-full sm:w-auto h-[52px] sm:h-[56px] mt-6 sm:mt-0 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm sm:text-base font-bold flex items-center justify-center gap-1.5 whitespace-nowrap"
                      data-testid="composer-rapid-save"
                      title={rapidReady ? 'Save the 4 required fields as a stub visit' : 'Fill name, 10-digit mobile, Came via and Location first'}>
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                      Rapid Save
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Edit mode: original layout preserved (identity fields are locked
                and already sourced from the visit row). */}
            {isEdit && (
              <>
                <Field label={`Patient name${identityLocked ? ' (entered by patient — locked)' : ''}`} required dpd={!isEdit}>
                  <input type="text" value={form.patient_name}
                    onChange={(e) => update('patient_name', e.target.value)}
                    readOnly={identityLocked}
                    className={`${fieldCls} ${identityLocked ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`}
                    data-testid="composer-patient-name" />
                </Field>
                <Field label={`Patient mobile${isEdit ? ' (locked — first source of truth)' : ''}`} required={!isEdit}>
                  <div className="space-y-1">
                    <input type="tel"
                      value={target?.patient_phone_masked || '— no phone on this row —'}
                      readOnly
                      className={`${fieldCls} bg-slate-50 text-slate-600 cursor-not-allowed font-mono`}
                      data-testid="composer-phone" />
                    <p className="text-[10px] text-slate-500" data-testid="composer-phone-lock-note">
                      <span className="inline-block mr-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                        {target?.phone_source === 'patient' ? 'Patient · OTP-verified' : target?.phone_source === 'doctor_otp' ? 'OTP-verified at log time' : 'Locked'}
                      </span>
                      Patient phone is sealed at first source of truth — non-editable by anyone, including the doctor.
                    </p>
                  </div>
                </Field>
                <Field label="Came via" required dpd={!isEdit}>
                  <select value={form.channel} onChange={(e) => update('channel', e.target.value)}
                    className={fieldCls} data-testid="composer-channel">
                    {(enums.channels || []).map(c => (
                      <option key={c} value={c}>{CHANNEL_LABEL[c] || c}</option>
                    ))}
                  </select>
                </Field>
              </>
            )}

            <Field label="Visit date" required dpd={!isEdit}>
              <input type="date" value={form.visit_date} onChange={(e) => update('visit_date', e.target.value)}
                className={fieldCls} data-testid="composer-date" />
            </Field>
            {(() => {
              const ageLocked = isEdit && target?.age_source === 'patient';
              return (
                <Field label={`Age (years)${ageLocked ? ' — patient-entered, locked' : ''}`} dpd={!isEdit || (!ageLocked && !!form.age)}>
                  <input type="number" min="0" max="120" inputMode="numeric"
                    value={form.age ?? ''}
                    readOnly={ageLocked}
                    onChange={(e) => update('age', e.target.value.replace(/\D/g, '').slice(0, 3))}
                    placeholder="e.g. 34"
                    className={`${fieldCls} ${ageLocked ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`}
                    data-testid="composer-age" />
                </Field>
              );
            })()}
            {showReferrer && (
              <Field label="Referrer name (free-text)">
                <input type="text" value={form.referrer_name} onChange={(e) => update('referrer_name', e.target.value)}
                  className={fieldCls} placeholder="e.g. Dr Mehta / Lilavati / Patient: Anita"
                  data-testid="composer-referrer-name" />
              </Field>
            )}
            <Field label="Condition/Symptom Reported" full dpd={!isEdit}>
              {(() => {
                const src = target?.symptom_source || 'none';
                const hasValue = !!(target?.primary_symptom);
                const locked = isEdit && hasValue && (src === 'patient' || src === 'doctor');
                const lockLabel = src === 'patient' ? 'Patient-Data · locked' : src === 'doctor' ? 'DpD · sealed' : '';
                return (
                  <div>
                    <input type="text" value={form.primary_symptom}
                      onChange={(e) => update('primary_symptom', e.target.value)}
                      readOnly={locked}
                      className={`${fieldCls} ${locked ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`}
                      placeholder="e.g. recurrent ear pain, bypass workup, follow-up after surgery"
                      data-testid="composer-symptom" />
                    {locked && (
                      <p className="mt-1 text-[10px] text-slate-500" data-testid="composer-symptom-lock-note">
                        <span className={`inline-block mr-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${src === 'patient' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-sky-50 text-sky-700 border border-sky-200'}`}>{lockLabel}</span>
                        {src === 'patient'
                          ? 'Patient entered this on check-in. Medical-record integrity — non-editable by anyone.'
                          : 'Doctor-stamped DpD on first save. Sealed — non-editable by both doctor and patient.'}
                      </p>
                    )}
                  </div>
                );
              })()}
            </Field>
            {/* Amount + entry-status row. Per founder spec (Feb 2026): the
                doctor — not channel heuristics — decides what counts as a
                real patient. `D` (Dummy) rows stay in the DB but are hidden
                from Patient Log, Amounts, and the future billing-receipt
                module. Both fields are editable any time after Save. */}
            <Field label="Amount (₹) — leave 0 if no payment yet">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">₹</span>
                <input
                  type="number"
                  min="0" max="99999" step="1"
                  inputMode="numeric" pattern="[0-9]*"
                  value={form.amount_paid ?? 0}
                  onChange={(e) => update('amount_paid', Math.max(0, Math.min(99999, parseInt(e.target.value.replace(/[^0-9]/g, '') || '0', 10))))}
                  placeholder="0"
                  className={`${fieldCls} pl-7`}
                  data-testid="composer-amount"
                />
              </div>
            </Field>
            <Field label="Entry status">
              <div className="grid grid-cols-2 gap-2" data-testid="composer-entry-status">
                <button
                  type="button"
                  onClick={() => update('entry_status', 'R')}
                  className={`px-3 py-3.5 rounded-xl border text-sm font-semibold transition-colors ${form.entry_status === 'R' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  data-testid="composer-entry-status-R"
                  aria-pressed={form.entry_status === 'R'}
                >
                  R · Real
                </button>
                <button
                  type="button"
                  onClick={() => update('entry_status', 'D')}
                  className={`px-3 py-3.5 rounded-xl border text-sm font-semibold transition-colors ${form.entry_status === 'D' ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}
                  data-testid="composer-entry-status-D"
                  aria-pressed={form.entry_status === 'D'}
                >
                  D · Dummy
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1.5 leading-snug">
                Dummy rows stay in your DB but are excluded from Patient Log totals, Amounts, and the future billing-receipt module. Flip at any time.
              </p>
            </Field>
            {/* Notes label flips dynamically. When the row is brand-new
                (doctor first-time entry), this IS the doctor's note.
                Once the patient self-verifies via QR check-in and adds
                their own note, the field becomes 'Patient's notes'. We
                read the existing source from `notes_source` on the
                visit; create mode is always DpD = doctor. */}
            {(() => {
              const isDoctorNote = !isEdit || (target?.notes_source || 'doctor') === 'doctor';
              return (
                <Field label={isDoctorNote ? "Doctor's notes" : "Patient's notes"} full dpd={!isEdit}>
                  <textarea rows={3} value={form.notes} onChange={(e) => update('notes', e.target.value)}
                    className={`${fieldCls} resize-y`} data-testid="composer-notes" />
                </Field>
              );
            })()}

            {/* ── Care Progress Monitor (per-patient · standard cases) ─────
                Doctor-only surface. Sits above the DHF composer so the
                doctor sees the patient's active care items first, can
                review the straight-line timeline, and set improvement
                milestones for the current care plan. No patient auth
                required — all data is DpD (this doctor's own clinical
                work-product). NOTE: This is the SIMPLE monitor for
                standard cases. The complex multi-thread monitor is a
                separate future module and reserved for that use. */}
            {isEdit && form.entry_status !== 'D' && !!target?.patient_phone_hash && (
              <div className="md:col-span-2 mt-2 rounded-2xl border border-emerald-200 bg-gradient-to-b from-emerald-50/40 to-white p-3" data-testid="dhf-threads-panel">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-800">
                    {(target?.patient_name || 'Patient')} · Care Progress Monitor
                  </p>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">Doctor-only</span>
                  {threadsLoading && <Loader2 size={11} className="animate-spin text-slate-400" />}
                </div>
                {(!threadsLoading && threads.length === 0) && (
                  <div className="px-1 pt-1 pb-0.5" data-testid="dhf-threads-empty">
                    <p className="text-[11px] text-slate-500 italic">No care items yet. When you seal a DHF with a condition on this patient, one opens here automatically.</p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <button
                        type="button" onClick={runBackfill} disabled={backfilling}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 hover:text-emerald-900 disabled:opacity-40"
                        data-testid="dhf-threads-backfill">
                        {backfilling ? <Loader2 size={10} className="animate-spin" /> : null}
                        Backfill from prior DHFs
                      </button>
                    </div>
                  </div>
                )}
                {backfillResult && (
                  <p className="text-[10px] text-slate-500 px-1 mt-1" data-testid="dhf-threads-backfill-result">{backfillResult}</p>
                )}
                {threads.length > 0 && (
                  <ul className="flex flex-wrap gap-2" data-testid="dhf-threads-chips">
                    {threads.map(t => {
                      const closed = t.status === 'cured' || t.status === 'concluded';
                      return (
                        <li key={t.id}>
                          <button
                            type="button"
                            onClick={() => setActiveThreadId(t.id)}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition ${closed ? 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200' : 'bg-white text-emerald-800 border-emerald-300 hover:bg-emerald-50'}`}
                            data-testid={`dhf-thread-chip-${t.id}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${closed ? 'bg-slate-400' : t.status === 'in_progress' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                            {t.name}
                            <span className="text-[9px] font-mono text-slate-500">·{t.visit_count || 0}v</span>
                            {closed && <span className="text-[9px] uppercase font-bold text-slate-500">{t.status}</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* ── Clinical File (Digital Health File) — edit mode only, and
                NEVER for Dummy (D) entries: a test record must not produce a
                sealed patient file. Dummy reverts to a plain "Save changes". ── */}
            {isEdit && form.entry_status !== 'D' && (
              <div className="md:col-span-2 mt-2">
                <ErrorBoundary fallbackTitle="Digital Health File composer temporarily unavailable">
                  <HealthFileSection target={target} onDraftChange={handleDhfDraftChange} existingThreads={threads} />
                </ErrorBoundary>
              </div>
            )}
          </div>

          {/* Read-only, append-only audit trail (consent + access fingerprint) */}
          {isEdit && (
            <div className="mt-2 rounded-2xl border border-slate-200 bg-slate-50/60 overflow-hidden" data-testid="patient-audit-trail">
              <div className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b border-slate-200 bg-white">
                <ShieldCheck size={16} className="text-emerald-600" />
                <span className="text-sm sm:text-base font-semibold text-slate-800">Audit trail</span>
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-400">read-only · non-editable</span>
              </div>
              {auditLoading ? (
                <div className="px-4 sm:px-5 py-4 text-sm text-slate-400">Loading…</div>
              ) : audit.length === 0 ? (
                <div className="px-4 sm:px-5 py-4 text-sm text-slate-400" data-testid="patient-audit-empty">No audit entries yet. Records appear here automatically when this patient self-checks-in via QR.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px] sm:text-base">
                    <thead>
                      <tr className="text-left text-slate-500 border-b border-slate-200">
                        <th className="px-4 sm:px-5 py-2 font-semibold whitespace-nowrap">Date &amp; time</th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap">Consent</th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap">IP address</th>
                        <th className="px-3 py-2 font-semibold whitespace-nowrap">Lat / Long</th>
                      </tr>
                    </thead>
                    <tbody>
                      {audit.map((a) => (
                        <tr key={a.id} className="border-b border-slate-100 last:border-0" data-testid="patient-audit-row">
                          <td className="px-4 sm:px-5 py-2 whitespace-nowrap text-slate-800">{a.ts ? new Date(a.ts).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap">{a.consent ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={13} /> Given</span> : '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono text-slate-600">{a.ip || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-mono text-slate-600">{(a.lat != null && a.lng != null) ? `${a.lat.toFixed(4)}, ${a.lng.toFixed(4)}` : 'n/a'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-2">
          {isEdit ? (
            <span className="text-[11px] sm:text-sm text-slate-400 italic flex items-center gap-1" data-testid="composer-nodelete-notice">
              <Trash2 size={11} className="opacity-40" /> Records cannot be deleted — edit notes/source only.
            </span>
          ) : <div />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2.5 rounded-full text-sm sm:text-lg text-slate-600 hover:bg-slate-100"
              data-testid="composer-cancel">
              Cancel
            </button>
            <button onClick={save} disabled={saving || !form.patient_name || !form.channel || !form.visit_date || (!isEdit && !phoneValid)}
              className="px-5 sm:px-7 py-2.5 sm:py-3 rounded-full bg-sky-600 text-white text-sm sm:text-lg font-medium hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="composer-save"
              title={!isEdit && !phoneValid ? 'Enter a 10-digit mobile number first' : ''}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {isEdit ? (form.entry_status === 'D' ? 'Save changes' : 'Draft Save · Clinical Data') : 'Add visit'}
            </button>
          </div>
        </div>
      </div>
      {/* Thread timeline modal — doctor-only progress monitor. */}
      {activeThreadId && (
        <ThreadTimeline threadId={activeThreadId} onClose={() => setActiveThreadId(null)} />
      )}
    </div>
  );
}

const fieldCls = 'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 sm:py-4 text-base sm:text-xl focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none';

function Field({ label, required, full, dpd, children }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-sm sm:text-lg font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
        <span>{label} {required && <span className="text-rose-500">*</span>}</span>
        {dpd && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200"
            title="DpD = Doctor-Provided Data. You're filling this in; it's unverified by the patient until their next QR check-in.">
            DpD
          </span>
        )}
      </label>
      {children}
    </div>
  );
}
